import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Button } from 'react-bootstrap';
import { FaStar, FaArrowLeft } from 'react-icons/fa';
import { getFacilityReviews } from '../../services/api';
import { toast } from 'react-toastify';
import './ReviewsModal.css';
import UserPlaceholder from '../../assets/svgs/user-placeholder.svg';

const ReviewsModal = ({ show, onHide, facility }) => {
  const [reviews, setReviews] = useState([]);
  const [overallRating, setOverallRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [ratingDistribution, setRatingDistribution] = useState({
    5: 0, 4: 0, 3: 0, 2: 0, 1: 0
  });
  const [allReviews, setAllReviews] = useState([]);

  useEffect(() => {
    if (show && facility?._id) {
      fetchAllReviews();
    }
  }, [show, facility]);

  useEffect(() => {
    if (show && facility?._id) {
      fetchReviews();
    }
  }, [show, facility, currentPage]);

  const fetchAllReviews = async () => {
    if (!facility?._id) return;
    
    setInitialLoading(true);
    try {
      // Fetch all reviews to calculate proper distribution
      const response = await getFacilityReviews(facility._id, 1, 1000); // Get all reviews
      const data = response.data;
      
      if (data.status) {
        setAllReviews(data.reviews);
        setOverallRating(data.overallRating);
        setTotalReviews(data.totalReviews);
        
        // Calculate rating distribution from all reviews
        const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        data.reviews.forEach(review => {
          const ratingValue = Number(review.rating);
          if (!isNaN(ratingValue) && ratingValue > 0) {
            const bucket = Math.min(5, Math.max(1, Math.floor(ratingValue)));
            distribution[bucket] = (distribution[bucket] || 0) + 1;
          }
        });
        setRatingDistribution(distribution);
      }
    } catch (error) {
      console.error('Error fetching all reviews:', error);
      toast.error('Failed to load rating distribution');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchReviews = async () => {
    if (!facility?._id) return;
    
    setLoading(true);
    try {
      const response = await getFacilityReviews(facility._id, currentPage, 10);
      const data = response.data;
      
      if (data.status) {
        setReviews(data.reviews);
        setHasNext(data.pagination.hasNext);
        setHasPrev(data.pagination.hasPrev);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating, size = 16) => {
    return (
      <div className="stars-container">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`star ${star <= rating ? 'filled' : 'empty'}`}
            size={size}
          />
        ))}
      </div>
    );
  };

  const getRatingPercentage = (rating) => {
    if (totalReviews === 0) return 0;
    return Math.round((ratingDistribution[rating] / totalReviews) * 100);
  };

  const formatTimeAgo = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const getUserDisplayName = (review) => {
    if (review.isAnonymous) {
      return 'Anonymous User';
    }
    if (review.user) {
      return `${review.user.firstName || ''} ${review.user.lastName || ''}`.trim() || 'User';
    }
    return 'User';
  };

  const getUserAvatar = (review) => {
    if (review.isAnonymous || !review.user?.profilePhoto) {
      return UserPlaceholder;
    }
    return review.user.profilePhoto;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="reviews-modal">
      <Modal.Header className="reviews-modal-header">
        <div className="header-content">
          <Button variant="link" className="back-button" onClick={onHide}>
            <FaArrowLeft size={20} />
          </Button>
          <div className="header-text">
            <h4 className="modal-title">Ratings & Reviews</h4>
            <div className="facility-name-reviews">
              {facility?.name}
              <span className="edit-icon">✏️</span>
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="reviews-modal-body">
        {initialLoading ? (
          <div className="loading-container">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Rating Distribution Section */}
            <div className="rating-distribution-section">
              <h5 className="section-title-review">Rating Distribution</h5>
              <Row>
                <Col md={4}>
                  <div className="overall-rating">
                    <div className="rating-number">{overallRating > 0 ? overallRating.toFixed(1) : 'N/A'}</div>
                    {overallRating > 0 ? renderStars(overallRating, 20) : null}
                    <div className="total-reviews">{totalReviews} reviews</div>
                  </div>
                </Col>
                <Col md={8}>
                  <div className="rating-breakdown">
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <div key={rating} className="rating-bar-row">
                        <div className="rating-label">
                          <FaStar className="star filled" size={14} />
                          {rating}
                        </div>
                        <div className="rating-bar-container">
                          <div 
                            className="rating-bar" 
                            style={{ 
                              width: `${getRatingPercentage(rating)}%`,
                              backgroundColor: getRatingPercentage(rating) > 0 ? '#28a745' : '#e9ecef'
                            }}
                          ></div>
                        </div>
                        <div className="rating-count">{ratingDistribution[rating]}</div>
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            </div>

            {/* User Reviews Section */}
            <div className="user-reviews-section">
              <h5 className="section-title-review">User Reviews</h5>
              {loading ? (
                <div className="loading-container">
                  <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : reviews.length === 0 ? (
                <div className="no-reviews">
                  <p>No reviews yet for this facility.</p>
                </div>
              ) : (
                <div className="reviews-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="review-item">
                      <div className="review-header">
                        <div className="reviewer-info">
                                                     <img 
                            src={getUserAvatar(review)} 
                            alt="User" 
                            className="reviewer-avatar"
                            onError={(e) => {
                              e.target.src = UserPlaceholder;
                            }}
                          />
                          <div className="reviewer-details">
                            <div className="reviewer-name">{getUserDisplayName(review)}</div>
                            <div className="review-time">{formatTimeAgo(review.time)}</div>
                          </div>
                        </div>
                        <div className="review-rating">
                          {renderStars(review.rating, 14)}
                        </div>
                      </div>
                      <div className="review-content">
                        <p className="review-comment">{review.comment}</p>
                        {review.categories && (
                          <div className="review-categories">
                            {Object.entries(review.categories).map(([category, rating]) => (
                              <div key={category} className="category-item">
                                <span className="category-name">{category}:</span>
                                <span className="category-rating">{rating}/5</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {(hasNext || hasPrev) && (
              <div className="pagination-container">
                <Button 
                  variant="outline-success" 
                  disabled={!hasPrev}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span className="page-info">Page {currentPage}</span>
                <Button 
                  variant="outline-success" 
                  disabled={!hasNext}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            )}

            {totalReviews === 0 && (
              <div className="no-reviews-overlay">
                <div className="no-reviews-glass">
                  <h5>No reviews yet</h5>
                  <p>Reviews will appear here once users start rating this facility.</p>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ReviewsModal; 